import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Table,
  Typography,
  DatePicker,
  Card,
  Row,
  Col,
  Tag,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { FilterFilled, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";
import { getDriverCallsRequest } from "../../redux/actions/driverCallsAction";
import { DriverCall as DriverCallRecord } from "../../redux/reducers/driverCallsReducer";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PRIMARY = "#86398f";

const CARD_GRADIENTS = [
  "linear-gradient(135deg,#86398f,#b56cc2)",
  "linear-gradient(135deg,#1bbf7a,#4be3a6)",
  "linear-gradient(135deg,#1677ff,#69a7ff)",
  "linear-gradient(135deg,#ff9f43,#ffc371)",
];

interface CabEntry {
  id: number;
  cabId: string;
  date: string;
  reason: string;
  remarks: string;
  type: string;
  updateBy: string;
}

const controlsCardBodyStyle: React.CSSProperties = {
  height: "100%",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
};

const tableCardBodyStyle: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: 12,
};

const GRID_STYLES = `
  .driver-call-table .ant-table-thead > tr > th,
  .driver-call-table .ant-table-tbody > tr > td {
    border-color: #d9d9d9 !important;
    text-align: center;
  }
  .driver-call-table .ant-table-tbody > tr > td {
    font-size: 15px !important;
  }
  .driver-call-table .ant-table-tbody > tr > td .ant-typography {
    font-size: 15px !important;
  }
  .driver-call-table .ant-table-tbody > tr > td .ant-tag {
    font-size: 14px !important;
  }
`;

const DriverCall: React.FC = () => {
  const dispatch = useDispatch();
  const { data = [], loading, total = 0 } = useSelector(
    (state: any) => state.driverCalls
  );
  const [searchQuery, setSearchQuery] = useState("");

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(),
    dayjs(),
  ]);

  const fetchDriverCalls = (
    dates: [Dayjs, Dayjs] = dateRange,
    signal?: AbortSignal
  ) => {
    if (dates?.[0] && dates?.[1]) {
      dispatch(
        getDriverCallsRequest(
          dates[0].format("YYYY-MM-DD"),
          dates[1].add(1, "day").format("YYYY-MM-DD"),
          signal
        )
      );
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchDriverCalls(dateRange, controller.signal);
    return () => controller.abort();
  }, [dateRange]);

  const incomingCount = data.filter(
    (item: DriverCallRecord) => item.type === "incoming"
  ).length;
  const outgoingCount = data.filter(
    (item: DriverCallRecord) => item.type === "outgoing"
  ).length;

  const disableFutureDates = (current: any) =>
    current && current > dayjs().endOf("day");

  const parseTimestamp = (dateStr?: string) => {
    if (!dateStr) return 0;
    const parsed = dayjs(dateStr);
    if (parsed.isValid()) return parsed.valueOf();
    const parsedCustom = dayjs(dateStr, "DD-MM-YYYY HH:mm:ss");
    if (parsedCustom.isValid()) return parsedCustom.valueOf();
    return 0;
  };

  const filteredData = [...data]
    .filter((item: DriverCallRecord) => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;

      return (
        String(item.id).toLowerCase().includes(query) ||
        item.cabId?.toLowerCase().includes(query)
      );
    })
    .sort((a: DriverCallRecord, b: DriverCallRecord) => {
      const timeA = parseTimestamp(a.date);
      const timeB = parseTimestamp(b.date);
      if (timeB !== timeA) {
        return timeB - timeA;
      }
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });

  const reasonFilters = useMemo(() => {
    const seen = new Map<string, string>();

    (data as DriverCallRecord[]).forEach((row) => {
      const raw = String(row?.reason ?? "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (seen.has(key)) return;
      const label = raw
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

      seen.set(key, label);
    });

    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, text]) => ({ text, value }));
  }, [data]);

  const columns: ColumnsType<CabEntry> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text: string) => (
        <Text style={{ color: "#666", fontWeight: 500 }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (text: string) => (
        <Text>{text?.split(" ")[0]}</Text>
      ),
    },
    {
      title: "Time",
      dataIndex: "date",
      key: "date",
      render: (text: string) => (
        <Text>{text?.split(" ").slice(1).join(" ")}</Text>
      ),
    },
    {
      title: "Cab ID",
      dataIndex: "cabId",
      key: "cabId",
      render: (text: string) => (
        <Tag
          color="purple"
          style={{
            borderRadius: 6,
            fontWeight: 600,
            padding: "2px 8px",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: [
        { text: "Incoming", value: "incoming" },
        { text: "Outgoing", value: "outgoing" },
      ],
      onFilter: (value: any, record: any) =>
        record.type === value,

      filterIcon: () => (
        <FilterFilled
          style={{
            color: "#fff",
            fontSize: 14,
          }}
        />
      ),

      render: (text: string) => (
        <Tag
          color={text === "incoming" ? "blue" : "green"}
          style={{
            borderRadius: 6,
            fontWeight: 500,
            padding: "2px 8px",
          }}
        >
          {text === "incoming"
            ? "Incoming 📥"
            : "Outgoing 📤"}
        </Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      filters: reasonFilters,
      filterSearch: reasonFilters.length > 8,
      onFilter: (value: any, record: any) =>
        String(record.reason ?? "").trim().toLowerCase() === value,

      filterIcon: () => (
        <FilterFilled
          style={{
            color: "#fff",
            fontSize: 14,
          }}
        />
      ),

      render: (text: string) => (
        <Text
          strong
          style={{
            color: "#444",
            textTransform: "capitalize",
          }}
        >
          {text?.replace(/_/g, " ")}
        </Text>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text: string) => (
        <Text style={{ color: "#555" }}>
          {text || "—"}
        </Text>
      ),
    },
    {
      title: "Updated By",
      dataIndex: "updateBy",
      key: "updateBy",
      render: (text: string) => (
        <Tag
          color="geekblue"
          style={{
            borderRadius: 6,
            fontWeight: 500,
            padding: "2px 8px",
          }}
        >
          {text}
        </Tag>
      ),
    },
  ];

  return (
    <div className="fit-page">
      <style>{GRID_STYLES}</style>

      <Row gutter={[12, 8]} style={{ flex: "0 0 auto" }}>
        {[
          { title: "Today Calls", value: total },
          { title: "Incoming", value: incomingCount },
          { title: "Outgoing", value: outgoingCount },
        ].map((card, i) => (
          <Col xs={12} lg={6} key={card.title}>
            <Card
              variant="borderless"
              styles={{ body: { padding: "10px 16px" } }}
              style={{
                height: "100%",
                borderRadius: 16,
                background: CARD_GRADIENTS[i],
                color: "#fff",
                boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              }}
            >
              <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 20 }}>
                {card.title}
              </Text>
              <Title level={3} style={{ color: "#fff", margin: 0 }}>
                {card.value}
              </Title>
            </Card>
          </Col>
        ))}

        <Col xs={24} lg={6}>
          <Card
            variant="borderless"
            styles={{ body: controlsCardBodyStyle }}
            style={{
              height: "100%",
              borderRadius: 16,
              background: "#fff",
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            }}
          >
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => fetchDriverCalls(dateRange)}
              loading={loading}
              style={{ background: PRIMARY, flex: "0 0 auto" }}
            >
              Refresh
            </Button>

            <Input.Search
              placeholder="Search ID or Cab ID..."
              allowClear
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ flex: "1 1 auto", minWidth: 0 }}
              enterButton={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  style={{ background: PRIMARY, borderColor: PRIMARY }}
                />
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        className="fit-table"
        styles={{ body: tableCardBodyStyle }}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          marginTop: 12,
          borderRadius: 10,
          boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
        }}
      >
        <Table
          className="driver-call-table"
          bordered
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey={(record: CabEntry) => record.id}
          size="middle"
          scroll={{ x: "max-content", y: 240 }}
          components={{
            header: {
              cell: ({ style, ...props }: any) => (
                <th
                  {...props}
                  style={{
                    ...style,
                    background: PRIMARY,
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                />
              ),
            },
          }}
          pagination={{
            pageSizeOptions: ["5", "10", "50", "100"],
            showSizeChanger: true,
            defaultPageSize: 10,
            showTotal: (count) => `Total ${count} calls`,
          }}
        />
      </Card>

    </div>
  );
};

export default DriverCall;
